import { TellescopeModelFromName, type useTellescopeMutation, type ReturnForMethod } from "@tellescope/tanstack-query";
import type { FormFieldNode, NextFieldLogicOptions } from "../../utils/types";
import { useCallback, useMemo, useRef } from "react";
import { FieldChangeOptions, FormTypesWithValues, TellescopeFieldResponse } from "../../types";
import {
  AnswerForType,
  DatabaseRecord,
  Enduser,
  FormField,
  FormFieldOptions,
  FormResponseValue,
} from "@tellescope/types-models";
import { expandSubmittedFieldIds } from "../../utils";
import { useTellescopeFormCore, type LocalTellescopeFormResponseMap } from "../use-tellescope-form-core";
import type { InterpolateFieldText } from "../use-tellescope-form-core/use-interpolate-field-text";
import { useGetFieldProps, type RepeatInputProps } from "./use-get-field-props";
import { useFormNavigation } from "./use-form-navigation";
import { useAutoAdvance } from "./use-auto-advance";
import { useSaveFieldResponse } from "./use-save-field-response";
import { useEnduserSubmitForm } from "./use-enduser-submit-form";
import { isNextDisabled } from "./is-next-disabled";
import type { FormShallowStoreHook, SubmitPhase } from "../../store";

export interface UseTellescopeEnduserFormArgs {
  formId: string;
  formTitle?: string;
  responseId?: string;
  enduserId: string;
  parentResponseId?: string;
  rootResponseId?: string;
  automationStepId?: string;
  startingFieldId?: string;
  existingResponses?: FormResponseValue[];
  urlLogicValue?: string;
  accessCode: string;
  enduser: TellescopeModelFromName<"endusers"> | Enduser;
  publicIdentifier?: string;
  useFormShallowStore: FormShallowStoreHook;
}

export interface UseTellescopeEnduserFormNoRoot {
  hasNoRootField: true;
}

/**
 * A validation error on a field that would be submitted, and so is holding the Submit button
 * disabled. One entry per navigable page: a Question Group's sub-field errors are reported against
 * the group, which is the page the enduser can actually return to.
 */
export interface BlockingFieldError {
  fieldId: string;
  /** The field's authored title, as the enduser saw it on the page. */
  title: string;
  error: string;
  /**
   * Whether `goToField` can take the enduser to this field — true when it is on the back-navigation
   * stack. A resumed form can carry an error on a field that was walked past without being pushed
   * onto that stack, which is reportable but not reachable.
   */
  isNavigable: boolean;
}

export const isFillableEnduserForm = (
  enduserForm: UseTellescopeEnduserFormReturn | UseTellescopeEnduserFormNoRoot | null
): enduserForm is UseTellescopeEnduserFormReturn => !!enduserForm && !("hasNoRootField" in enduserForm);

export interface UseTellescopeEnduserFormReturn {
  currentResponses: LocalTellescopeFormResponseMap;
  interpolateFieldText: InterpolateFieldText;
  validateField: <T extends keyof AnswerForType = keyof AnswerForType>(args: {
    fieldId: string;
    valueOverride?: AnswerForType[T];
  }) => Promise<string | null>;
  validateAllFields: () => Promise<boolean>;
  visibleFields: (FormField & { answer: TellescopeFieldResponse["answer"] })[];
  setFieldValue: <T extends FormTypesWithValues>(
    fieldId: string,
    value: AnswerForType[T],
    changeOptions?: FieldChangeOptions
  ) => void;
  saveResponse: () => Promise<void>;
  submitForm: () => Promise<ReturnForMethod<"form_responses", "submit_form_response">>;
  submitFormMutation: ReturnType<typeof useTellescopeMutation<"form_responses", "submit_form_response">>;
  submitPhase: SubmitPhase;
  isAutoSubmitting: boolean;
  autoSubmitted: boolean;
  autoSubmitError: boolean;
  serverRedirectUrl: string | undefined;
  uploadProgress: { completed: number; total: number };
  form: TellescopeModelFromName<"forms"> | undefined;
  fieldErrors: Record<string, string | null>;
  hasBlockingFieldError: boolean;
  blockingFieldErrors: BlockingFieldError[];
  touchedFields: Set<string>;
  handleFieldBlur: (fieldId: string) => void;
  getFieldProps: <T extends FormTypesWithValues = FormTypesWithValues>(
    fieldId: string,
    type: T
  ) => {
    id: string;
    type: T;
    title: string;
    value: AnswerForType[T];
    onChange: (newValue: AnswerForType[T], changeOptions?: FieldChangeOptions) => void;
    onBlur?: () => void;
    error: string | null;
    touched: boolean;
    options?: FormFieldOptions;
    fullZIP?: boolean;
    repeat?: RepeatInputProps;
    disabled?: boolean;
    pendingFiles?: File[];
    onPendingFilesChange?: (files: File[]) => void;
  } | null;
  enduser: TellescopeModelFromName<"endusers"> | Enduser | undefined;
  isComplete: boolean;
  isSubmitted: boolean;
  activeField: FormFieldNode;
  currentFieldResponse: TellescopeFieldResponse | undefined;
  goToNextField: () => Promise<void>;
  goToPreviousField: () => void;
  /**
   * Navigates back to a field the enduser has already visited, as if they had pressed Back until
   * they reached it. A no-op for any id that is not on the back-navigation stack.
   */
  goToField: (fieldId: string) => void;
  isNextDisabled: boolean;
  isPreviousDisabled: boolean;
  showSubmit: boolean;
  currentPageIndex: number;
  totalPages: number;
  progress: number;
  getNumberOfRemainingPages: () => number;
  saveFieldResponse: () => Promise<void>;
  fieldsArr: TellescopeModelFromName<"form_fields">[];
  logicOptions: NextFieldLogicOptions;
  applyDatabasePrepopulation: (records: DatabaseRecord[]) => void;
  setCustomerId: (customerId: string) => void;
  bookedAppointmentUrl: string | undefined;
  setBookedAppointmentUrl: (bookedAppointmentUrl: string | undefined) => void;
}

export const useTellescopeEnduserForm = ({
  formId,
  formTitle,
  responseId,
  enduserId,
  parentResponseId,
  rootResponseId,
  automationStepId,
  startingFieldId,
  existingResponses,
  urlLogicValue,
  accessCode,
  enduser,
  publicIdentifier,
  useFormShallowStore,
}: UseTellescopeEnduserFormArgs): UseTellescopeEnduserFormReturn | UseTellescopeEnduserFormNoRoot | null => {
  const core = useTellescopeFormCore({
    formId,
    formTitle,
    responseId,
    enduserId,
    parentResponseId,
    rootResponseId,
    existingResponses,
    urlLogicValue,
    accessCode,
    enduser,
    publicIdentifier,
    useFormShallowStore,
  });

  const { response } = core.enduserResponseInfoQuery.data ?? {};
  const form = core.form;

  const fieldsMap = useMemo(() => core.fields?.fieldsMap ?? {}, [core.fields?.fieldsMap]);

  const {
    saveActiveFieldAndSubFields,
    saveImmediateFieldResponse,
    saveFieldResponse: saveFieldResponseFor,
  } = useSaveFieldResponse({ core, responseId, responseAccessCode: response?.accessCode });

  const {
    resolvedActiveField,
    hasNoRootField,
    includedFieldIds,
    resolvedPrevStack,
    goToField,
    currentPageIndex,
    totalPages,
    progress,
    goToNextFieldInternal,
    goToPreviousField,
    isPreviousDisabled,
    showSubmit,
    getNumberOfRemainingPages,
  } = useFormNavigation({
    fieldsArr: core.fields?.fieldsArr,
    fieldsMap,
    subFieldIds: core.subFieldIds,
    currentResponses: core.currentResponses,
    responsesMap: core.responsesMap,
    logicOptions: core.logicOptions,
    existingResponses,
    startingFieldId,
    onBeforeAdvance: saveActiveFieldAndSubFields,
    responseId: responseId ?? response?.id,
    responseAccessCode: response?.accessCode,
    useFormShallowStore,
  });

  const {
    setFieldValueWithAutoAdvance,
    submitFormRef,
    isAutoSubmitting,
    autoSubmitted,
    autoSubmitError,
    serverRedirectUrl,
  } = useAutoAdvance({
    setFieldValue: core.setFieldValue,
    fieldsMap,
    currentResponses: core.currentResponses,
    repeatMismatchFieldIds: core.repeatMismatchFieldIds,
    logicOptions: core.logicOptions,
    activeField: resolvedActiveField,
    goToNextFieldInternal,
    saveImmediateFieldResponse,
    useFormShallowStore,
  });

  const advancingFromFieldIdRef = useRef<string | undefined>(undefined);

  const goToNextField = useCallback(async () => {
    if (!resolvedActiveField) return;
    // `validateField` is awaited, so a second call started before it resolves would close over the
    // same active field, pass validation again, and advance twice — skipping a field. Latch on the
    // field id, which clears as soon as the wizard actually moves.
    if (advancingFromFieldIdRef.current === resolvedActiveField.value.id) return;
    advancingFromFieldIdRef.current = resolvedActiveField.value.id;

    try {
      const error = await core.validateField({ fieldId: resolvedActiveField.value.id });
      if (error) return;

      if (core.repeatMismatchFieldIds.has(resolvedActiveField.value.id)) {
        core.handleRepeatBlur(resolvedActiveField.value.id);
        return;
      }

      goToNextFieldInternal();
    } finally {
      advancingFromFieldIdRef.current = undefined;
    }
  }, [resolvedActiveField, core, goToNextFieldInternal]);

  const nextDisabled = useMemo(
    () =>
      isNextDisabled({
        activeField: resolvedActiveField,
        fieldErrors: core.fieldErrors,
        repeatMismatchFieldIds: core.repeatMismatchFieldIds,
        isFieldValueValid: core.isFieldValueValid,
      }),
    [resolvedActiveField, core]
  );

  const saveFieldResponse = useCallback(
    () => saveFieldResponseFor(resolvedActiveField),
    [saveFieldResponseFor, resolvedActiveField]
  );

  const submitForm = useEnduserSubmitForm({
    core,
    activeField: resolvedActiveField,
    includedFieldIds,
    fieldsMap,
    existingResponses,
    response,
    formId,
    enduserId,
    parentResponseId,
    rootResponseId,
    automationStepId,
    useFormShallowStore,
  });

  submitFormRef.current = submitForm;

  const blockingFieldErrors = useMemo((): BlockingFieldError[] => {
    const submittedFieldIds = expandSubmittedFieldIds(includedFieldIds, fieldsMap, core.isSubFieldHidden);

    // The enduser's own path through the form, which is the order the list reads in. A sub-field's
    // error is rolled up onto its parent group by `syncGroupErrorForSubField`, so reporting the
    // sub-field too would say the same thing twice — and it has no page of its own to return to.
    const pathIndexById = resolvedPrevStack.reduce<Record<string, number>>(
      (acc, field, index) => ({ ...acc, [field.value.id]: index }),
      {}
    );

    return Array.from(submittedFieldIds)
      .reduce<BlockingFieldError[]>((acc, fieldId) => {
        const error = core.fieldErrors[fieldId];
        if (!error || core.subFieldIds.has(fieldId)) return acc;
        return [
          ...acc,
          { fieldId, title: fieldsMap[fieldId]?.title ?? "", error, isNavigable: fieldId in pathIndexById },
        ];
      }, [])
      .sort(
        (a, b) =>
          (pathIndexById[a.fieldId] ?? Number.MAX_SAFE_INTEGER) - (pathIndexById[b.fieldId] ?? Number.MAX_SAFE_INTEGER)
      );
  }, [includedFieldIds, fieldsMap, core.isSubFieldHidden, core.fieldErrors, core.subFieldIds, resolvedPrevStack]);

  const hasBlockingFieldError = blockingFieldErrors.length > 0;

  const getFieldProps = useGetFieldProps({ core, setFieldValueWithAutoAdvance });

  const isSubmitted = !!response?.submittedAt || core.submitFormMutation.isSuccess;

  if (!resolvedActiveField) return hasNoRootField ? { hasNoRootField: true } : null;

  return {
    currentResponses: core.currentResponses,
    interpolateFieldText: core.interpolateFieldText,
    validateField: core.validateField,
    validateAllFields: core.validateAllFields,
    visibleFields: core.visibleFields,
    setFieldValue: setFieldValueWithAutoAdvance,
    saveResponse: core.saveResponse,
    submitForm,
    submitFormMutation: core.submitFormMutation,
    submitPhase: core.submitPhase,
    isAutoSubmitting,
    autoSubmitted,
    autoSubmitError,
    serverRedirectUrl,
    uploadProgress: core.uploadProgress,
    form,
    fieldErrors: core.fieldErrors,
    hasBlockingFieldError,
    blockingFieldErrors,
    touchedFields: core.touchedFields,
    handleFieldBlur: core.handleFieldBlur,
    getFieldProps,
    applyDatabasePrepopulation: core.applyDatabasePrepopulation,
    setCustomerId: core.setCustomerId,
    bookedAppointmentUrl: core.bookedAppointmentUrl,
    setBookedAppointmentUrl: core.setBookedAppointmentUrl,
    enduser: core.enduser,
    isComplete: core.isComplete,
    isSubmitted,
    activeField: resolvedActiveField,
    currentFieldResponse: core.currentResponses[resolvedActiveField.value.id],
    goToNextField,
    goToPreviousField,
    goToField,
    isNextDisabled: nextDisabled,
    isPreviousDisabled,
    showSubmit,
    currentPageIndex,
    totalPages,
    progress,
    getNumberOfRemainingPages,
    saveFieldResponse,
    fieldsArr: core.fields?.fieldsArr ?? [],
    logicOptions: core.logicOptions,
  };
};
